import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useLocation, useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserActivityLogs = () => {
  const [filterAction, setFilterAction] = useState("all");
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [refreshSnackbar, setRefreshSnackbar] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, linkId } = location.state;

  // --------------------------
  // 🔹 Fetch Activity Logs
  // --------------------------
  const fetchLogs = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE_URL}/api/auth/activity/user/${user?.user_id || user?.id}`
      );

      const data = await res.json();
      const logs = data.data || [];

      const filtered = logs.filter((log) => log.linkid === linkId) || [];

      if (data.success) {
        setActivityLogs(filtered);
      } else {
        setError(data.message || "Failed to fetch logs");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong while fetching logs");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.user_id && !user?.id) return;
    fetchLogs();
  }, [user]);

  // --------------------------
  // 🔹 Refresh Handler
  // --------------------------
  const handleRefresh = async () => {
    await fetchLogs();
    setRefreshSnackbar(true);
  };

  // 🔹 User-friendly action labels
  const actionLabels = {
    LIKED_PRODUCT: "Liked Stock",
    VIEW_STOCK: "Viewed Stock",
    ADD_CART: "Added to Cart",
    REMOVE_CART: "Removed from Cart",
    LOGIN: "Login",
    REQUEST_ACCESS: "Access Requested",
  };

  // 🔹 Format Time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    const h = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${d}-${m}-${y} : ${h}:${min}`;
  };

  // 🔹 Unique actions dropdown
  const uniqueActions = useMemo(() => {
    const all = activityLogs.map((a) => a.action_type);
    return [...new Set(all)];
  }, [activityLogs]);

  // 🔹 Final filtered logs
  const filteredLogs = useMemo(() => {
    if (filterAction === "all") return activityLogs;
    return activityLogs.filter((a) => a.action_type === filterAction);
  }, [filterAction, activityLogs]);

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        p: { xs: 2, sm: 4, md: 6 },
      }}
    >
      {/* =======================
          TOP BUTTONS (Back + Home)
      ========================== */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 20, fill: "#42A996" }} />
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate("/admin-dash")}
        >
          <HomeRoundedIcon sx={{ fontSize: 20, fill: "#42A996" }} />
        </Box>
      </Box>

      {/* =======================
            USER INFO + FILTERS
      ========================== */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          color: "#fff",
        }}
      >
        <Box>
          <Typography>Name : {user?.user_name}</Typography>
          <Typography>Mobile : {user?.mobile}</Typography>
        </Box>

        {/* FILTER + REFRESH */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            size="small"
            sx={{ bgcolor: "#fff", minWidth: 220, borderRadius: 1 }}
          >
            <MenuItem value="all">All Actions</MenuItem>
            {uniqueActions.map((action) => (
              <MenuItem key={action} value={action}>
                {actionLabels[action] || action}
              </MenuItem>
            ))}
          </Select>

          <IconButton
            onClick={handleRefresh}
            sx={{
              color: "#42A996",
              bgcolor: "#fff",
              borderRadius: 1,
              "&:hover": { color: "#3b927f" },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* =======================
            TABLE / LOADER
      ========================== */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: "60vh" }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: "#2A3A4B",
            borderRadius: 2,
            overflowX: "auto", // ✔ only table scrolls
            WebkitOverflowScrolling: "touch",
            "& table": {
              minWidth: 900, // ✔ prevents shrinking
              tableLayout: "auto", // ✔ keeps natural width
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
                <TableCell
                  sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Time Stamp
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Link Title
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Tag No
                </TableCell>
                <TableCell
                  sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      color: "#fff",
                      textAlign: "center",
                      py: 3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    No activity logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log, index) => (
                  <TableRow
                    key={log.id || index}
                    sx={{
                      bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                    }}
                  >
                    <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                      {formatDateTime(log.created_at)}
                    </TableCell>

                    <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                      {log.link_name || "-"}
                    </TableCell>

                    <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                      {log.tag_no || "-"}
                    </TableCell>

                    <TableCell sx={{ color: "#fff", whiteSpace: "nowrap" }}>
                      {actionLabels[log.action_type] || log.action_type}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setOpenSnackbar(false)}
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Refresh Notification */}
      <Snackbar
        open={refreshSnackbar}
        autoHideDuration={2000}
        onClose={() => setRefreshSnackbar(false)}
        message="Logs refreshed ✅"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default UserActivityLogs;
